import { postToServer } from "@/client/utils/postToServer";
import { uploadFilesToS3 } from "@/client/utils/s3Upload";
import type { PresetSearchIndex, PresetSearchItem } from "@/shared/audio-to-preset-search.api";
import { kAudioToPresetSearchEndpoint } from "@/shared/audio-to-preset-search.api";
import { createErrorMessage } from "@/shared/utils/error";

export interface AudioToPresetSearchParams {
    file: File;
    k: number;
    index?: PresetSearchIndex;
}

export interface AudioToPresetSearchResultData {
    uuids: string[];
    urls: string[];
    sourceIndexes: string[];
    /** Catalog names and plugins for the matches the gateway knew, when the server could ask. */
    items?: PresetSearchItem[];
}

export interface AudioToPresetSearchService {
    search: (params: AudioToPresetSearchParams) => Promise<AudioToPresetSearchResultData | Error>;
}

export const createAudioToPresetSearchService = (): AudioToPresetSearchService => {
    const search = async (
        params: AudioToPresetSearchParams,
    ): Promise<AudioToPresetSearchResultData | Error> => {
        try {
            const keysOrError = await uploadFilesToS3({ fileKey: params.file });
            if (keysOrError instanceof Error) {
                return keysOrError;
            }

            const result = await postToServer(kAudioToPresetSearchEndpoint, {
                fileKey: keysOrError.fileKey,
                k: params.k,
                index: params.index,
            });
            if (result instanceof Error) {
                return result;
            }

            return {
                uuids: result.uuids,
                urls: result.urls,
                sourceIndexes: result.sourceIndexes,
                ...(result.items !== undefined ? { items: result.items } : {}),
            };
        } catch (error) {
            return new Error(createErrorMessage(error));
        }
    };

    return { search };
};
